import type { PriceLookup } from "./pricing-table";

/**
 * API-equivalent cost, computed from token counts and a price lookup.
 *
 * The lookup is a parameter rather than a module import so this file stays free
 * of I/O and runs unchanged in the browser. Cost has to be derived at *read*
 * time — that is what lets a price correction repair historical figures without
 * re-collecting anything — so wherever the dashboard renders, this is the code
 * that has to run there.
 */

/** Token counts for a single turn, used to compute API-equivalent cost. */
export interface TokenUsage {
  inputTokens: number;
  outputTokens: number;
  cacheCreate5mTokens: number;
  cacheCreate1hTokens: number;
  cacheReadTokens: number;
}

/**
 * API-equivalent cost in USD for a model + token usage. Cache writes are billed
 * by TTL (5-minute vs 1-hour) at distinct rates; unknown models cost 0.
 */
export function costForUsage(
  lookup: PriceLookup,
  model: string | null,
  u: TokenUsage,
): number {
  const p = lookup(model);
  if (!p) return 0;
  return (
    u.inputTokens * p.input +
    u.outputTokens * p.output +
    u.cacheCreate5mTokens * p.cacheWrite +
    u.cacheCreate1hTokens * p.cacheWrite1h +
    u.cacheReadTokens * p.cacheRead
  );
}
